import React, { useContext, useEffect, useState, useMemo } from "react";
import useSearch from "hooks/useSearch";
import { ReactComponent as SelectArrow } from "assets/svgs/select-arrow.svg";
import lockBodyScroll from "@helpers/lockBodyScroll";
import Icon from "@root/_components/Icon/icon";
import Link from "@root/helpers/link";
import { TELEPHONE_NUMBER } from "@root/utils/constants";
import { ProductFinderContext } from "@root/context/product-finder";

const Search = ({ search, handleSearchChange }) => (
    <div className="modal-dropdown__search">
        <div className="relative">
            <input
                type="text"
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Search..."
                className="modal-dropdown__search--input"
            />
            <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
        </div>
    </div>
);

export const ModalDropdown = ({
    selected,
    options,
    optionProperty,
    onSelect,
    onGoBack,
    onSwitchStep,
    onCloseModal,
    hasSearch,
    onOpen,
    showModal,
    disabled,
    className,
    dropdownsArr,
    hideToggler,
    topOptions = [],
}) => {
    const { search, resetSearch, filteredOptions, handleSearchChange } = useSearch(options, optionProperty);
    const { productFinder, setProductFinder, errorField, setErrorField } = useContext(ProductFinderContext);

    // When topOptions has items, split the (search-filtered) options into a
    // "Top Items" group and an "Others" group.
    const hasTopOptions = Array.isArray(topOptions) && topOptions?.length > 0;
    const { topGroup, otherGroup, hasGroups } = useMemo(() => {
        if (!hasTopOptions) {
            return { topGroup: [], otherGroup: filteredOptions, hasGroups: false };
        }
        const topValues = new Set(topOptions.map((option) => option[optionProperty]));
        let top = filteredOptions.filter((option) => topValues.has(option[optionProperty]));
        top = top.sort((a, b) => a.count - b.count);
        // Keep the top items inside the full "All" list as well, so they are not
        // removed from the complete set of options.
        const others = filteredOptions;
        return { topGroup: top, otherGroup: others, hasGroups: top.length > 0 };
    }, [filteredOptions, topOptions, optionProperty, hasTopOptions]);

    const steps = dropdownsArr?.map((item) => item.name);
    const hasSteps = Array.isArray(steps) && steps.length > 0;

    const currentStepIndex = steps?.findIndex((step) => step.toLowerCase() === optionProperty);

    const [isCollapsed, setIsCollapsed] = useState(false);

    useEffect(() => {
        setIsCollapsed(false);
    }, [showModal, currentStepIndex]);

    const handleCloseModal = () => {
        resetSearch();
        setErrorField(null);
        if (onCloseModal) {
            onCloseModal();
        } else if (onGoBack) {
            onGoBack(optionProperty);
        }
    };

    const handleSwitchStep = (stepName) => {
        if (!onSwitchStep) return;
        resetSearch();
        setIsCollapsed(false);
        onSwitchStep(stepName);
    };

    const handleSelect = (dropdownName, selectedValue) => {
        onSelect(dropdownName, selectedValue);
        resetSearch();

        setErrorField(null);
    };

    const renderOption = (option, keyPrefix = "") => (
        <button
            key={`${keyPrefix}${option[optionProperty]}`}
            type="button"
            className="flex justify-between items-center py-4 px-4 text-left text-base border-b border-gray-200 hover:bg-gray-50 modal-dropdown__option"
            onClick={() => handleSelect(optionProperty, option[optionProperty])}
        >
            <span>{option[optionProperty]}</span>
        </button>
    );

    const renderOptionsContent = () => (
        <>
            {hasSearch && (
                <Search
                    search={search}
                    handleSearchChange={handleSearchChange}
                />
            )}

            <div className="modal-dropdown__options-list flex flex-col">
                {hasGroups ? (
                    <>
                        {topGroup.length > 0 && (
                            <>
                                <div className="modal-dropdown__options-group-label">
                                    Popular {optionProperty}s
                                </div>
                                {topGroup.map((option) => renderOption(option, "top-"))}
                            </>
                        )}
                        {otherGroup.length > 0 && (
                            <>
                                <div className="modal-dropdown__options-group-label">
                                    All {optionProperty}s
                                </div>
                                {otherGroup.map((option) => renderOption(option, "all-"))}
                            </>
                        )}
                    </>
                ) : (
                    filteredOptions.map((option) => renderOption(option))
                )}
            </div>
        </>
    );


    lockBodyScroll(showModal, true);

    const getStepValue = (stepName) => {
        const dropdownItem = dropdownsArr?.find((item) => item.name === stepName.toLowerCase());
        return {
            label: stepName,
            value: dropdownItem?.selected,
        }
    };

    const getStepHasOptions = (stepName) => {
        const dropdownItem = dropdownsArr?.find((item) => item.name === stepName.toLowerCase());
        return (dropdownItem?.list?.length || 0) > 0;
    };

    const getStepHasValue = (stepName) => {
        const dropdownItem = dropdownsArr?.find((item) => item.name === stepName.toLowerCase());
        return Boolean(dropdownItem?.selected);
    };

    const getFirstUnfilledStepName = () => {
        const firstUnfilled = steps?.find((step) => !getStepHasValue(step));
        return firstUnfilled ? firstUnfilled.toLowerCase() : null;
    };

    const stickyIndexByStepIndex = {};
    let filledAboveCurrentCount = 0;
    if (steps && currentStepIndex >= 0) {
        steps.forEach((step, index) => {
            if (index >= currentStepIndex) return;
            if (!getStepHasValue(step)) return;
            stickyIndexByStepIndex[index] = filledAboveCurrentCount;
            filledAboveCurrentCount += 1;
        });
    }

    return (
        <>
            {!hideToggler && (
                <div
                    role="button"
                    tabIndex="-1"
                    className={`finder-mobile__dropdown flex flex-col gap-2 ${disabled ? "opacity-70" : ""}`}
                >
                    <div className={`modal-dropdown__toggler flex flex-col md:py-2.5 py-1.5 px-2 w-full bg-white rounded-lg relative shadow-richTextBoxShadow z-100 ${className}`}>
                        <div
                            role="button"
                            tabIndex="-1"
                            onKeyDown={(event) => {
                                event.stopPropagation();
                                if (onOpen) onOpen(event);
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (onOpen) onOpen(event);
                            }}
                            className="text-gray-701 text-left px-2 md:text-base text-base-xs"
                        >
                            {selected}
                        </div>
                        <SelectArrow className="absolute w-2.5 h-2.5 right-8 md:top-5 top-4" />
                        {/* Different arrow for new style */}
                        <svg className="modal-dropdown__toggler-arrow" width="6" height="14" viewBox="0 0 6 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.749999 0.75L4.75 6.75L0.75 12.75" stroke="#00344E" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>
                </div>
            )}

            {showModal && (
                <div
                    className="w-full h-full min-h-screen min-w-screen fixed z-20 top-0 left-0 right-0 bg-white shadow-[0_100px_0_0_rgb(255,255,255)] z-[200]"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    role="presentation"
                >
                    <div className="overflow-y-scroll h-full flex-col flex">
                        <div className="flex flex-col">
                            <div className="modal-dropdown__content">
                                <button type="button" onClick={handleCloseModal} aria-label="Close">
                                    <Icon icon="chevron-left" size={30} color="#00344E" />
                                </button>
                                <img src="https://cdn.sanity.io/images/4swbd7aj/pre-prod/f97554c7af75237a5b89d6438daca9c756b0a303-240x92.png?w=240&h=92&auto=format" alt="Seal Skin Logo" />
                                <Link to={TELEPHONE_NUMBER} aria-label="Call Us" className="header__actions--phone">
                                    <Icon icon="phone-call" color="#00334e" size={24} />
                                </Link>
                            </div>
                            {!hasSteps && (
                                <div className="modal-dropdown__simple-panel">
                                    {renderOptionsContent()}
                                </div>
                            )}
                            {hasSteps && (
                            <div className="modal-dropdown__steps">
                                {steps.map((step, index) => {
                                    const stepName = step.toLowerCase();
                                    const isCurrentStep = index === currentStepIndex;
                                    const stepValue = getStepValue(step);
                                    const stepHasOptions = getStepHasOptions(step);
                                    const stepHasValue = getStepHasValue(step);
                                    const isExpanded = isCurrentStep && !isCollapsed;
                                    const canToggleCurrent = isCurrentStep;
                                    const canSwitchStep = !isCurrentStep && (stepHasOptions || stepHasValue);
                                    const isInteractive = canToggleCurrent || canSwitchStep;
                                    const isDisabled = !isCurrentStep && !stepHasOptions && !stepHasValue;
                                    const stickyIndex = stickyIndexByStepIndex[index];
                                    const isSticky = typeof stickyIndex === "number";
                                    const stickyStyleIndex = isSticky
                                        ? stickyIndex
                                        : (isExpanded ? filledAboveCurrentCount : null);
                                    const stickyInlineStyle = stickyStyleIndex !== null
                                        ? { "--sticky-index": stickyStyleIndex }
                                        : undefined;

                                    const handleStepClick = () => {
                                        if (canToggleCurrent) {
                                            if (isCollapsed) {
                                                setIsCollapsed(false);
                                                return;
                                            }
                                            const nextUnfilledStep = getFirstUnfilledStepName();
                                            if (nextUnfilledStep && nextUnfilledStep !== stepName) {
                                                handleSwitchStep(nextUnfilledStep);
                                            }
                                            return;
                                        }
                                        if (canSwitchStep) {
                                            handleSwitchStep(stepName);
                                        }
                                    };

                                    return (
                                        <div
                                            key={step}
                                            className={`modal-dropdown__step ${isExpanded ? "modal-dropdown__step--active " : ""}${isDisabled ? "modal-dropdown__step--disabled " : ""}${isSticky ? "modal-dropdown__step--sticky " : ""} ${hasGroups ? "modal-dropdown__step--has-groups" : ""}`}
                                            style={stickyInlineStyle}
                                        >
                                            <div
                                                className="modal-dropdown__step-header"
                                                onClick={isInteractive ? handleStepClick : undefined}
                                                role="button"
                                                tabIndex={isInteractive ? 0 : -1}
                                                aria-expanded={isExpanded}
                                                aria-disabled={!isInteractive || undefined}
                                                onKeyDown={(e) => {
                                                    if (isInteractive && (e.key === "Enter" || e.key === " ")) {
                                                        e.preventDefault();
                                                        handleStepClick();
                                                    }
                                                }}
                                            >
                                                <span className="modal-dropdown__steps-text">
                                                    <span className="modal-dropdown__steps-text-label">
                                                        {stepValue.label}

                                                        {isCurrentStep && (
                                                            <div className="modal-dropdown__steps-current"></div>
                                                        )}
                                                    </span>
                                                    <span className="modal-dropdown__steps-text-value">
                                                        {stepValue.value}
                                                    </span>
                                                </span>
                                                <svg className="modal-dropdown__steps-current-arrow" xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
                                                    <path d="M0.799804 0.800782L6.7998 6.80078L12.7998 0.800781" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
                                                </svg>
                                            </div>

                                            {isExpanded && (
                                                <div className="modal-dropdown__step-panel">
                                                    {errorField && (
                                                        <div className="product-page-patio-summary__error">
                                                            <p className="product-page-patio-summary__error-text">
                                                                Please select {errorField.toLowerCase() === "option" ? "an" : "a"} {errorField.toLowerCase()}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {renderOptionsContent()}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ModalDropdown;
